import { addDoc, collection } from 'firebase/firestore';
import { db } from './firebase';
import type { ShareLaunchPayload } from '../utils/sharePayload';

const LOG_PREFIX = '[ShareImport]';
const MAX_FIELD_LENGTH = 800;

export type ShareImportLogOutcome =
    | 'failure'
    | 'exception'
    | 'no_url'
    | 'success';

export interface ShareImportLogInput {
    readonly userId: string;
    readonly outcome: ShareImportLogOutcome;
    readonly stage: string;
    readonly code?: string;
    readonly messageKey?: string;
    readonly rawPayload?: ShareLaunchPayload;
    readonly extractedUrl?: string | null;
    readonly normalizedUrl?: string | null;
    readonly shareContextText?: string;
    readonly errorMessage?: string;
    readonly parseMeta?: {
        readonly ingredientCount?: number;
        readonly instructionCount?: number;
        readonly isAiEligible?: boolean;
        readonly hasEncryptedKey?: boolean;
    };
}

function truncate(value: string | undefined | null, max = MAX_FIELD_LENGTH): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.length <= max) return trimmed;
    return `${trimmed.slice(0, max)}… [truncated ${trimmed.length - max} chars]`;
}

function sanitizePayload(payload: ShareLaunchPayload | undefined): ShareLaunchPayload | undefined {
    if (!payload) return undefined;
    return {
        title: truncate(payload.title, 300),
        text: truncate(payload.text),
        url: truncate(payload.url),
    };
}

function buildLogDocument(input: ShareImportLogInput): Record<string, unknown> {
    return {
        userId: input.userId,
        outcome: input.outcome,
        stage: input.stage,
        ...(input.code ? { code: input.code } : {}),
        ...(input.messageKey ? { messageKey: input.messageKey } : {}),
        ...(input.rawPayload ? { rawPayload: sanitizePayload(input.rawPayload) } : {}),
        ...(input.rawPayload?.rawSearch ? { rawSearch: truncate(input.rawPayload.rawSearch, 2000) } : {}),
        ...(input.extractedUrl !== undefined ? { extractedUrl: input.extractedUrl ?? null } : {}),
        ...(input.normalizedUrl !== undefined ? { normalizedUrl: input.normalizedUrl ?? null } : {}),
        ...(input.shareContextText ? { shareContextText: truncate(input.shareContextText) } : {}),
        ...(input.errorMessage ? { errorMessage: truncate(input.errorMessage, 1200) } : {}),
        ...(input.parseMeta ? { parseMeta: input.parseMeta } : {}),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        createdAt: new Date().toISOString(),
    };
}

export async function logShareImportEvent(input: ShareImportLogInput): Promise<void> {
    const document = buildLogDocument(input);

    if (input.outcome === 'failure' || input.outcome === 'exception' || input.outcome === 'no_url') {
        console.error(`${LOG_PREFIX}:${input.outcome}`, document);
    } else {
        console.info(`${LOG_PREFIX}:${input.outcome}`, document);
    }

    try {
        await addDoc(collection(db, 'users', input.userId, 'shareImportLogs'), document);
    } catch (error) {
        console.error(`${LOG_PREFIX}: failed to persist log`, error, document);
    }
}
